import { baseQuery } from "./baseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";

export const billingService = createApi({
  reducerPath: "billingService",
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getBillings: builder.query<
      any,
      { page: number; limit: number; skip_log?: boolean; merchantId: number }
    >({
      query: ({ merchantId, skip_log, ...others }) => ({
        url: `/billings/${merchantId}`,
        method: "GET",
        params: others,
      }),
    }),
    getBillingsByMerchant: builder.query<
      any,
      {merchantId: number; limit: number}
    >({
      query: ({ merchantId,...others}) => ({
        url: `/merchant-card/${merchantId}`,
        method: "GET",
        params: others,
      }),
    }),
    makePayment: builder.mutation<
      any,
      {
        id: number;
      }
    >({
      query: (body: any) => ({
        url: `/billings/${body.id}/make-payment`,
        body,
        method: "PUT",
        headers: {
          PageName: "Merchant Settings",
        },
      }),
    }),
    selectCard: builder.mutation<any,any>({
      query: (body: any) => ({
        url: `/merchant-card/card-selection/${body?.token}`,
        body,
        method: "PUT",
        headers: {
          PageName: "Merchant Settings",
        },
      }),
    }),
    deleteCard: builder.mutation<
      any,
      {
        merchantId: number;
        token: string;
      }
    >({
      query: (body: any) => ({
        url: `/merchant-card/merchant/${body.merchantId}/card/${body.token}`,
        body,
        method: "DELETE",
        headers: {
          PageName: "Merchant Settings",
        },
      }),
    }),
    downloadInvoice: builder.query<any, { merchantId: number }>({
      query: ({ merchantId }) => ({
        url: `/billings/${merchantId}/download-invoice`,
        method: "GET",
        headers: {
          PageName: "Merchant Settings",
        },
        responseHandler: async (response) => {
          const url = window.URL.createObjectURL(await response.blob());
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `Invoice.pdf`);
          link.setAttribute("target", "_blank");
          document.body.appendChild(link);
          link.click();
        },
      }),
    }),
    downloadExcel: builder.query<any, { merchantId: number }>({
      query: ({ merchantId }) => ({
        url: `/billings/${merchantId}/download-excel`,
        method: "GET",
        headers: {
          PageName: "Merchant Settings",
        },
      }),
    }),
    downloadReceipt: builder.query<any, { merchantId: number }>({
      query: ({ merchantId }) => ({
        url: `/billings/${merchantId}/download-receipt`,
        method: "GET",
        headers: {
          PageName: "Merchant Settings",
        },
        responseHandler: async (response) => {
          const url = window.URL.createObjectURL(await response.blob());
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `Receipt.pdf`);
          link.setAttribute("target", "_blank");
          document.body.appendChild(link);
          link.click();
        },
      }),
    }),
    getPaymentMethods: builder.query<any, { merchantId: number }>({
      query: ({ merchantId }) => ({
        url: `/payment-methods/${merchantId}`,
        method: "GET",
        headers: {
          PageName: "Merchant Settings",
        },
      }),
    }),
    createPaymentMethod: builder.mutation<
      any,
      {
        card_type: string;
        card_name: string;
        expire_year: string;
        expire_month: string;
        branch: string;
        country: string;
        status: string;
        card_number_last_4: string;
        vault_token: string;
        merchant_id: string;
      }
    >({
      query: (body: any) => ({
        url: "/payment-methods",
        body,
        method: "POST",
        headers: {
          PageName: "Merchant Setting",
        },
      }),
    }),
    updatePaymentMethod: builder.mutation<
      any,
      {
        id: string;
        card_type: string;
        card_name: string;
        expire_year: string;
        expire_month: string;
        branch: string;
        country: string;
        status: string;
        card_number_last_4: string;
        vault_token: string;
        merchant_id: string;
      }
    >({
      query: (body: any) => ({
        url: `/payment-methods/${body.id}`,
        body,
        method: "PUT",
        headers: {
          PageName: "Merchant Setting",
        },
      }),
    }),
    deletePaymentMethod: builder.mutation<
      any,
      {
        id: string;
        merchantId: number;
      }
    >({
      query: (body: any) => ({
        url: `/payment-methods/${body.merchantId}/${body.id}`,
        body,
        method: "DELETE",
        headers: {
          PageName: "Merchant Setting",
        },
      }),
    }),
  }),
});

export const {
  useGetBillingsQuery,
  useGetPaymentMethodsQuery,
  useLazyGetBillingsQuery,
  useGetBillingsByMerchantQuery,
  useLazyGetBillingsByMerchantQuery,
  useCreatePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useDeletePaymentMethodMutation,
  useLazyDownloadInvoiceQuery,
  useLazyDownloadReceiptQuery,
  useMakePaymentMutation,
  useLazyDownloadExcelQuery,
  useSelectCardMutation,
  useDeleteCardMutation
} = billingService;
